import './BufferCell.scss';

import { IconEdit } from '@hulu-react-style-components/icons';
import { User } from 'mission-control-common-components';
import React, { memo, useState } from 'react';
import { useToggle } from 'react-use';

import ModalPopup from '../../../../../common/ModalPopup';
import { onLineItemFailureUpdate } from '../../../../../common/utils';
import type { PermissionsNames } from '../../../../../constants';
import type { Nullable } from '../../../../../models';
import bem from '../../../../../utils/bem';
import { formatPercentage } from '../../../../../utils/formatting';
import { useUpdateLineItem } from '../../hooks';
import BufferEditInput from './BufferEditInput';
import BufferLabel from './BufferLabel';

const AUTOMATED_TOOLTIP =
  'The buffer value displayed is automatically generated for optimal delivery performance for the line item. This is the default setting and recommended.';

const USER_APPLIED_TOOLTIP =
  'This line is opted out of automated buffers. Click the Edit icon to set a buffer value for the line item (optional).';

type Props = Readonly<{
  automateBuffer: Nullable<boolean>;
  automatedDeliveryBufferPercent: Nullable<number>;
  deliveryBufferPercent: Nullable<number>;
  id: string;
  isSOV?: boolean;
}>;

const [block, element] = bem('buffer-cell');

function formatBufferValue(value: Nullable<number>): string {
  if (value === null || value === undefined) return formatPercentage(0);
  return formatPercentage(value);
}

const CONFIRMATION_MESSAGE = 'Are you sure you want to update the buffer value?';
const CANCEL_BUTTON_LABEL = 'Cancel';
const CONFIRMATION_BUTTON_LABEL = 'Confirm';

function BufferCell({
  automateBuffer,
  automatedDeliveryBufferPercent,
  deliveryBufferPercent,
  id,
  isSOV = false,
}: Props): React.JSX.Element {
  const permissions = User.getPermissions<PermissionsNames>();
  const { updateLineItemField, loading } = useUpdateLineItem(id, 'deliveryBufferPercent', onLineItemFailureUpdate);
  const [active, toggleEdit] = useToggle(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [pendingValue, setPendingValue] = useState<number>(0);

  const isNull = automateBuffer === null;
  const isOptIn = automateBuffer === true;
  const isDisabled = isNull || isOptIn || !permissions?.updateLineItem;
  const userAppliedDisplay = formatBufferValue(deliveryBufferPercent);

  const handleEdit = (): void => {
    if (isDisabled) return;
    toggleEdit(true);
  };

  const handleEditSubmit = (value: number): void => {
    setPendingValue(value);
    setIsModalVisible(true);
  };

  const handleEditCancel = (): void => {
    toggleEdit(false);
  };

  const handleConfirm = (): void => {
    updateLineItemField(pendingValue);
    setIsModalVisible(false);
    toggleEdit(false);
  };

  const handleModalCancel = (): void => {
    setIsModalVisible(false);
  };

  const renderUserApplied = (): React.JSX.Element => {
    if (active) {
      return (
        <BufferEditInput
          initialValue={deliveryBufferPercent?.toString() ?? '0'}
          onSubmit={handleEditSubmit}
          onCancel={handleEditCancel}
        />
      );
    }

    return (
      <button
        className={element('percent', isDisabled ? 'disabled' : '')}
        onClick={handleEdit}
        disabled={isDisabled}
        data-testid="buffer-user-applied"
      >
        <span className={element('percent-value', isDisabled ? 'disabled' : '')}>{userAppliedDisplay}</span>
        {!isDisabled ? <IconEdit className={element('percent-icon')} /> : <span className={element('icon-spacer')} />}
      </button>
    );
  };

  return (
    <div className={block()} data-testid="buffer-cell">
      <div className={element('row')}>
        <BufferLabel
          label="Automated"
          tooltipId={`${id}_automated_info`}
          tooltipMessage={AUTOMATED_TOOLTIP}
          showTooltip={!isNull && isOptIn}
          disabled={isSOV || (!isNull && !isOptIn)}
        />
        <span className={element('percent')}>
          <span className={element('percent-value', !isOptIn ? 'disabled' : '')}>
            {formatBufferValue(automatedDeliveryBufferPercent)}
          </span>
          <span className={element('icon-spacer')} />
        </span>
      </div>
      <div className={element('row')}>
        <BufferLabel
          label="User-Applied"
          tooltipId={`${id}_user_applied_info`}
          tooltipMessage={USER_APPLIED_TOOLTIP}
          showTooltip={!isNull && !isOptIn}
          disabled={isNull || isOptIn}
        />
        {renderUserApplied()}
      </div>
      <ModalPopup
        visible={isModalVisible}
        onCancel={handleModalCancel}
        onOk={handleConfirm}
        modalProps={{
          title: CONFIRMATION_MESSAGE,
          cancelText: CANCEL_BUTTON_LABEL,
          okText: CONFIRMATION_BUTTON_LABEL,
          okButtonProps: {
            disabled: loading,
            loading: loading,
          },
        }}
      />
    </div>
  );
}

export default memo(BufferCell);
